import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Clock, XCircle, Loader2 } from "lucide-react";
import { Logo } from "./Logo";
import { formatPrice } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = {
  exitoso: {
    icon: Check,
    cls: "bg-amber-600",
    title: "¡Pago recibido!",
    text: "Gracias por tu compra. Te contactaremos para coordinar la entrega de tus piezas.",
  },
  pendiente: {
    icon: Clock,
    cls: "bg-stone-500",
    title: "Pago en proceso",
    text: "Mercado Pago está confirmando tu pago. Te avisaremos en cuanto se acredite.",
  },
  fallido: {
    icon: XCircle,
    cls: "bg-red-500",
    title: "El pago no se completó",
    text: "No se realizó ningún cargo. Puedes intentarlo de nuevo con otro método de pago.",
  },
};

export const PaymentResult = () => {
  const path = window.location.pathname;
  const kind = path.includes("exitoso") ? "exitoso" : path.includes("pendiente") ? "pendiente" : "fallido";
  const [order, setOrder] = useState(null);
  const state = STATES[kind];
  const Icon = state.icon;

  useEffect(() => {
    const orderId = localStorage.getItem("hylasu-pending-order");
    if (kind === "exitoso") {
      localStorage.removeItem("hylasu-cart");
      localStorage.removeItem("hylasu-pending-order");
    }
    if (orderId) {
      axios.get(`${API}/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [kind]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDFBF7] px-6 text-center dark:bg-[#0C0A09]">
      <Logo />
      <motion.div
        data-testid="payment-result"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 flex max-w-md flex-col items-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
          className={`flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg ${state.cls}`}
        >
          <Icon size={36} />
        </motion.div>
        <h1 className="mt-6 font-serif text-3xl font-light text-stone-900 dark:text-stone-50 sm:text-4xl">{state.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-stone-500 dark:text-stone-400">{state.text}</p>
        {order && (
          <div className="mt-6 rounded-2xl border border-stone-200 px-6 py-4 dark:border-stone-700">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-400">Pedido</p>
            <p data-testid="payment-order-number" className="mt-1 font-mono text-lg text-amber-700 dark:text-amber-400">{order.order_number}</p>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              Total: <span className="font-semibold">{formatPrice(order.total)}</span>
              {" · Estado: "}
              <span data-testid="payment-order-status" className="font-semibold">{order.status === "pagado" ? "Pagado" : order.status === "pendiente" ? "Por confirmar" : order.status}</span>
            </p>
          </div>
        )}
        {!order && kind === "exitoso" && <Loader2 size={18} className="mt-6 animate-spin text-stone-400" />}
        <a
          data-testid="payment-result-home"
          href="/"
          className="mt-10 rounded-full bg-stone-900 px-8 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
        >
          Volver a la tienda
        </a>
      </motion.div>
    </div>
  );
};
